import { Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Reveal } from '../components/Reveal';
import { usePageTitle } from '../hooks/usePageTitle';
import { FOUNDATION, ASSESSMENT_URL, COURSES_URL } from '../config/foundation';

export function Home() {
  usePageTitle('Empowering people to reach their full potential');
  return (
    <Layout>
      {/* ───────────────────────── Hero ───────────────────────── */}
      <section className="relative overflow-hidden isolate bg-mustard-green-dark">
        {/* Background photograph — diverse hands together (community & growth).
            Source: Pexels (free license, no attribution required) — photo 461049. */}
        <div
          className="absolute inset-0 bg-cover bg-center scale-105"
          style={{ backgroundImage: 'url(/hero-community.jpg)' }}
          aria-hidden="true"
        />
        {/* Brand-green overlay keeps the white text readable over the photo */}
        <div className="absolute inset-0 bg-gradient-to-br from-mustard-green-dark/95 via-mustard-green-dark/88 to-mustard-green/75" />
        {/* Floating colour glows */}
        <div className="absolute -top-24 -right-16 w-[26rem] h-[26rem] rounded-full bg-mustard-gold/20 blur-3xl animate-float" aria-hidden="true" />
        <div className="absolute -bottom-32 -left-24 w-[26rem] h-[26rem] rounded-full bg-mustard-green-mid/25 blur-3xl animate-float-slow" aria-hidden="true" />
        {/* Film grain */}
        <div className="absolute inset-0 noise opacity-[0.06] mix-blend-overlay" aria-hidden="true" />
        {/* Cream fade into the next section */}
        <div className="absolute -bottom-px left-0 right-0 h-28 bg-gradient-to-t from-mustard-cream to-transparent" aria-hidden="true" />

        <div className="section relative py-24 sm:py-32 lg:py-40">
          <div className="max-w-3xl animate-fade-up">
            <span className="glass inline-flex items-center gap-2 rounded-full px-4 py-1.5 mb-6 text-xs font-semibold tracking-[0.12em] uppercase text-white/90">
              <span className="w-1.5 h-1.5 rounded-full bg-mustard-gold animate-glow-pulse" />
              A registered charity &mdash; est. 2019
            </span>
            <h1 className="font-sans text-hero-sm sm:text-hero font-bold text-white mb-6 tracking-tight">
              Empowering people to reach their{' '}
              <span className="text-mustard-gold">
                full potential.
              </span>
            </h1>
            <p className="text-base sm:text-lg text-white/75 leading-relaxed mb-9 max-w-xl">
              We empower women to build independence, and train the next generation of technology
              leaders in underserved communities &mdash; so the people we lift go on to lift others.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link to="/apply" className="btn-gold !py-3.5 !px-8 text-center">
                Apply for Support
              </Link>
              <Link
                to="/get-involved"
                className="glass px-8 py-3.5 text-white rounded-full font-semibold text-sm hover:bg-white/20 transition-all text-center"
              >
                Support Our Work
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ──────────────── Our focus areas (two pillars) ──────────────── */}
      <section className="bg-white py-16 sm:py-24 border-b border-slate-100">
        <div className="section">
          <Reveal className="text-center max-w-2xl mx-auto mb-12 sm:mb-14">
            <p className="eyebrow mb-3">Our Focus Areas</p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-display">
              <span className="text-gradient">Two missions, one spark</span>
            </h2>
            <p className="text-slate-500 mt-4 leading-relaxed">
              Everything we do flows from two commitments &mdash; lifting women toward independence,
              and closing the digital divide for underserved communities.
            </p>
          </Reveal>
          <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
            <Reveal>
              <PillarFeature
                image="/pillar-women.jpg"
                tag="Women Empowerment"
                title="Access, skills and confidence for women"
                desc="We give women the resources, training and support to become valued, self-sustaining members of their communities — and to mentor others as they rise."
                to="/what-we-do"
                cta="How we help"
              />
            </Reveal>
            <Reveal delay={120}>
              <PillarFeature
                image="/pillar-tech.jpg"
                tag="TechLifeSpark"
                title="Training the next technology leaders"
                desc="We bring technical knowledge into underserved communities — closing the digital divide and equipping future technology leaders with job-ready skills."
                href={COURSES_URL}
                cta="Explore our courses"
              />
            </Reveal>
          </div>
        </div>
      </section>

      {/* ──────────────── How we create change ──────────────── */}
      <section className="py-16 sm:py-24">
        <div className="section">
          <Reveal className="text-center mb-12 sm:mb-16">
            <p className="eyebrow mb-3">Our Approach</p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-display">
              <span className="text-gradient">How we create lasting change</span>
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {[
              { step: '01', title: 'Up-skill', desc: 'We equip people with practical skills, structured coaching, and the confidence to take control of their direction in work and life.' },
              { step: '02', title: 'Leverage', desc: 'We help people recognise and apply the strengths they already have — turning potential into sustainable income and independence.' },
              { step: '03', title: 'Lift others', desc: 'Those we empower become mentors and advocates, so support spreads through families, communities, and networks.' },
            ].map((p, i) => (
              <Reveal key={p.step} delay={i * 120}>
                <PillarCard {...p} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ──────────────── Who we are ──────────────── */}
      <section className="relative bg-white py-16 sm:py-24 border-y border-slate-100">
        <div className="section">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <Reveal>
              <p className="eyebrow mb-3">Who We Are</p>
              <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold text-mustard-green-dark tracking-display mb-5">
                A charity built around people, not paperwork
              </h2>
              <p className="text-slate-500 leading-relaxed mb-4">
                {FOUNDATION.name} &mdash; formerly {FOUNDATION.formerName} &mdash; is a Charitable
                Incorporated Organisation working across England, Wales and Scotland. We exist to
                remove the barriers that stop people from building the life and livelihood they are
                capable of.
              </p>
              <p className="text-slate-500 leading-relaxed mb-6">
                We do this through education and training, practical advice, grants, and our flagship
                LifeSpark360&#8482; programme &mdash; combining purpose-built technology with real human
                coaching and mentoring.
              </p>
              <Link to="/about" className="btn-secondary inline-block">Read our story</Link>
            </Reveal>
            <Reveal delay={120} className="grid grid-cols-2 gap-4">
              <MiniCard label="Education &amp; training" />
              <MiniCard label="Relief of poverty" />
              <MiniCard label="Employment &amp; enterprise" />
              <MiniCard label="Advice &amp; advocacy" />
              <MiniCard label="Grants &amp; finance" />
              <MiniCard label="Mentoring &amp; community" />
            </Reveal>
          </div>
        </div>
      </section>

      {/* ──────────────── Flagship programme ──────────────── */}
      <section className="py-16 sm:py-24">
        <div className="section">
          <Reveal>
            <div className="relative overflow-hidden rounded-3xl bg-brand-gradient shadow-glow-green">
              <div className="absolute inset-0 noise opacity-[0.07] mix-blend-overlay" aria-hidden="true" />
              <div className="absolute -top-20 -right-10 w-80 h-80 rounded-full bg-mustard-gold/20 blur-3xl animate-float" aria-hidden="true" />
              <div className="relative grid grid-cols-1 lg:grid-cols-2">
                <div className="p-8 sm:p-12 lg:p-16">
                  <p className="text-mustard-gold text-xs font-semibold tracking-[0.15em] uppercase mb-3">Our Flagship Programme</p>
                  <h2 className="font-display text-3xl sm:text-4xl font-bold text-white mb-4 tracking-display">
                    LifeSpark360&#8482;
                  </h2>
                  <p className="text-white/70 leading-relaxed mb-7 text-sm sm:text-base">
                    A structured 12-week coaching journey built on the SPARK Method. It helps people see
                    themselves clearly, build new foundations, sharpen their CV and skills, grow their
                    confidence, and sustain momentum &mdash; with a feeder Seedbed programme for those
                    starting out.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Link to="/programmes" className="btn-gold text-center">Explore LifeSpark360</Link>
                    <a href={ASSESSMENT_URL} className="glass px-6 py-3 text-white rounded-xl font-semibold text-sm hover:bg-white/20 transition-all text-center">
                      Take the Readiness Assessment
                    </a>
                  </div>
                </div>
                <div className="p-8 sm:p-12 lg:p-16 flex flex-col justify-center gap-3 border-t lg:border-t-0 lg:border-l border-white/10">
                  {[
                    { letter: 'S', title: 'See Yourself Clearly' },
                    { letter: 'P', title: 'Plant New Roots' },
                    { letter: 'A', title: 'Arm Yourself' },
                    { letter: 'R', title: 'Rise to the Room' },
                    { letter: 'K', title: 'Keep the Flame' },
                  ].map((phase) => (
                    <div key={phase.letter} className="group flex items-center gap-4 rounded-xl px-3 py-2 -mx-3 hover:bg-white/5 transition-colors">
                      <div className="w-10 h-10 rounded-xl bg-gold-gradient text-mustard-green-dark flex items-center justify-center flex-shrink-0 shadow-lg shadow-black/10 group-hover:scale-105 transition-transform">
                        <span className="font-display text-lg font-bold">{phase.letter}</span>
                      </div>
                      <span className="text-white font-medium text-sm">{phase.title}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ──────────────── Courses ──────────────── */}
      <section className="bg-white py-16 sm:py-24 border-y border-slate-100">
        <div className="section">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <Reveal>
              <p className="eyebrow mb-3">Online Courses</p>
              <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold text-mustard-green-dark tracking-display mb-5">
                Practical skills, at your own pace
              </h2>
              <p className="text-slate-500 leading-relaxed mb-6">
                Alongside our coaching programmes, we publish self-paced online courses that build
                real, job-ready skills &mdash; so you can learn on your own schedule and put it to work
                straight away.
              </p>
              <a href={COURSES_URL} target="_blank" rel="noopener noreferrer" className="btn-primary inline-flex items-center gap-2">
                Browse our courses
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" d="M14 5h5v5m0-5L10 14M9 5H5v14h14v-4" /></svg>
              </a>
            </Reveal>
            <Reveal delay={120}>
              <a
                href={COURSES_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="group card block p-7 sm:p-8 relative overflow-hidden"
              >
                <div className="absolute top-0 left-0 right-0 h-1 bg-gold-gradient" aria-hidden="true" />
                <span className="badge bg-mustard-gold/15 text-mustard-gold mb-4">Featured course</span>
                <h3 className="font-display text-xl font-bold text-mustard-green-dark mb-2">
                  IT Budgeting for IT Service Managers
                </h3>
                <p className="text-sm text-slate-500 leading-relaxed mb-5">
                  A focused, practical course on planning, defending, and managing an IT budget &mdash;
                  built for service managers who want to lead with financial confidence.
                </p>
                <span className="inline-flex items-center gap-1.5 text-mustard-green font-semibold text-sm">
                  Start the course
                  <span className="transition-transform group-hover:translate-x-1">&rarr;</span>
                </span>
              </a>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ──────────────── Get involved ──────────────── */}
      <section className="py-16 sm:py-24">
        <div className="section">
          <Reveal className="text-center mb-12">
            <p className="eyebrow mb-3">Get Involved</p>
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-display">
              <span className="text-gradient">Every spark needs support to grow</span>
            </h2>
            <p className="text-slate-500 mt-3 max-w-xl mx-auto">
              Whether you give time, skills, or funding, you help someone take the next step toward
              independence &mdash; and the confidence to lift others.
            </p>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { title: 'Support our work', desc: 'Help fund places on our programmes and the resources that make them possible.', cta: 'Support us' },
              { title: 'Volunteer &amp; mentor', desc: "Share your experience by coaching, mentoring, or supporting a participant's journey.", cta: 'Get involved' },
              { title: 'Partner with us', desc: 'Charities, employers, and community groups can collaborate to reach more people.', cta: 'Partner with us' },
            ].map((a, i) => (
              <Reveal key={a.title} delay={i * 120}>
                <ActionCard {...a} to="/get-involved" />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ──────────────── Final CTA ──────────────── */}
      <section className="pb-16 sm:pb-24">
        <div className="section">
          <Reveal>
            <div className="relative overflow-hidden rounded-3xl bg-brand-gradient shadow-glow-green text-center p-10 sm:p-16">
              <div className="absolute inset-0 noise opacity-[0.07] mix-blend-overlay" aria-hidden="true" />
              <div className="absolute -bottom-24 left-1/2 -translate-x-1/2 w-[30rem] h-[30rem] rounded-full bg-mustard-gold/15 blur-3xl animate-glow-pulse" aria-hidden="true" />
              <div className="relative">
                <h2 className="font-display text-2xl sm:text-4xl font-bold text-white mb-4 tracking-display">
                  Ready to take the next step?
                </h2>
                <p className="text-white/70 mb-8 max-w-md mx-auto">
                  Find out whether our programmes are right for you, and how to apply for a place.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Link to="/apply" className="btn-gold !py-3.5 !px-8">Apply for Support</Link>
                  <a href={ASSESSMENT_URL} className="glass px-8 py-3.5 text-white rounded-xl font-semibold text-sm hover:bg-white/20 transition-all">
                    Take the Assessment
                  </a>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </Layout>
  );
}

function PillarFeature({
  image, tag, title, desc, to, href, cta,
}: {
  image: string; tag: string; title: string; desc: string; to?: string; href?: string; cta: string;
}) {
  const cls = 'group card h-full overflow-hidden block';
  const inner = (
    <>
      <div className="relative h-56 sm:h-64 overflow-hidden">
        <img src={image} alt={tag} loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
        <div className="absolute inset-0 bg-gradient-to-t from-mustard-green-dark/45 to-transparent" aria-hidden="true" />
        <span className="badge absolute top-4 left-4 bg-white/90 text-mustard-green-dark backdrop-blur-sm">{tag}</span>
      </div>
      <div className="p-7 sm:p-8">
        <h3 className="font-display text-xl sm:text-2xl font-bold text-mustard-green-dark mb-2">{title}</h3>
        <p className="text-sm text-slate-500 leading-relaxed mb-5">{desc}</p>
        <span className="inline-flex items-center gap-1.5 text-mustard-green font-semibold text-sm">
          {cta}
          <span className="transition-transform group-hover:translate-x-1">&rarr;</span>
        </span>
      </div>
    </>
  );
  return href ? (
    <a href={href} target="_blank" rel="noopener noreferrer" className={cls}>{inner}</a>
  ) : (
    <Link to={to!} className={cls}>{inner}</Link>
  );
}

function PillarCard({ step, title, desc }: { step: string; title: string; desc: string }) {
  return (
    <div className="group card h-full p-7 sm:p-8 relative overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-mustard-green-mid to-mustard-gold opacity-0 group-hover:opacity-100 transition-opacity" aria-hidden="true" />
      <div className="w-12 h-12 rounded-2xl bg-brand-gradient text-mustard-gold flex items-center justify-center mb-5 font-display font-bold shadow-lg shadow-mustard-green-dark/15">
        {step}
      </div>
      <h3 className="font-display text-lg font-bold text-mustard-green-dark mb-2">{title}</h3>
      <p className="text-sm text-slate-500 leading-relaxed" dangerouslySetInnerHTML={{ __html: desc }} />
    </div>
  );
}

function MiniCard({ label }: { label: string }) {
  return (
    <div className="p-4 rounded-xl border border-slate-100 bg-mustard-cream/50 flex items-center gap-2.5 hover:border-mustard-green-mid/40 hover:bg-mustard-cream transition-colors">
      <span className="w-2 h-2 rounded-full bg-gold-gradient flex-shrink-0" />
      <span className="text-xs sm:text-sm font-medium text-mustard-green-dark" dangerouslySetInnerHTML={{ __html: label }} />
    </div>
  );
}

function ActionCard({ title, desc, to, cta }: { title: string; desc: string; to: string; cta: string }) {
  return (
    <div className="group card h-full p-7 sm:p-8 flex flex-col">
      <h3 className="font-display text-lg font-bold text-mustard-green-dark mb-2" dangerouslySetInnerHTML={{ __html: title }} />
      <p className="text-sm text-slate-500 leading-relaxed mb-5 flex-1">{desc}</p>
      <Link to={to} className="inline-flex items-center gap-1.5 text-mustard-green font-semibold text-sm">
        {cta}
        <span className="transition-transform group-hover:translate-x-1">&rarr;</span>
      </Link>
    </div>
  );
}
